import { BCMSRecord, BCMSStateManagerCRUD, useBCMSContext, useModel, useParams, useRoute, useStateManager } from "@bcm-dev/bcms-vite";
import React from 'react';

import Util from 'classes/Util';
import { ContentSubheaderView, PageContainer, PageContentView, WidgetContainer } from 'components/shared';
import ExternalIframe from 'components/shared/containers/ExternalIframe';
import ProfilePicker from 'components/shared/widgets/ProfilePicker';
import Classes, { Profile } from "model";
import "./styles.css";
import "./mobile.css";


/**
 * @function ChangeProfilePage
 * @return {React.Component}
 * @exported true
 * @description The change profile page
 */

export default function ChangeProfilePage(props) {
  const navigate = useRoute();
  const { user } = useModel();
  const { profileID, profileType } = useParams();
  let modifiedProfileID = profileID;
  let modifiedProfileType = profileType;
  // Branding options needed for captures
  const branding = useModel().branding.options;
  window.BRANDING = { "zle_abi22" : branding.zle_abi22 };
  // Initalize all model classes
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);
  const profiles = Profile.initAll();
  
  // Check if only one profile is available and load it by default.
  // That profile may not match the params in the url which is what was 
  // originally used to set the content in this page.
  if(profiles.length === 1) {
    const brandedProfile = profiles[0];
    const brandedProfileType = brandedProfile.type;
    modifiedProfileID = brandedProfile.id;
    modifiedProfileType = brandedProfileType;
  }

  const selectedProfileID = Profile.initByID(modifiedProfileID) 
  ? modifiedProfileID
  : Profile.getDefaultBrandedProfile(context.randing).id;

  // Sets the values for the data and ui variables
  const data = { ...getDefaultPageData(selectedProfileID, modifiedProfileType, context), ...props.data ?? {} };
  const ui = { ...getDefaultPageUI(data.transfer), ...props.ui ?? {} };
  // The state to be passed to the state manager
  const state = { data, ui };
  
  // :: React.state, React.setState -> OBJECT
  // Function to initalize the controller for the state manager
  const controller = (state, setState) => ({
      data: state.data, // Set the data property of the controller
      ui: state.ui, // Set the ui property of the controller
      
      // Event listeners.
      onChange: profileID => {
        const selectedProfile = Profile.initByID(profileID);

        BCMSStateManagerCRUD.init(state)
        .update([ "data", "profileID" ], selectedProfile.id)
        .submit(setState);
      },

      onSelectClick: () => {
        const selectedProfile = Profile.initByID(data.profileID);

        // Update prop "profileType" on "Select" button click only.
        // The zelle iframe will read any changes made to it and update.
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "profileType" ], selectedProfile.type)
        .submit(setState);

        // If we are in abi22 standalone, updated the zelle iframe.
        if(data.showZLEIframe) {
          // Update the profile type in the url. 
          // This url in abi22 will be read by the zle iframe in order to 
          // determine whether to show personal or business functionality.
          navigate(`/changeProfile/${ data.profileType }/${ data.profileID }`);

        // Else, we are in mabi23, so navigate to the zelle page.
        } else {
          navigate(`/zelle/${ data.profileType }`);
        }
      }
  });

  /* Page body is removed for specific captures, so this class is used to do that. */
  const noBackgroundClass = ui.hidePageBody ? "no-background" : "";
  
  return(
    <PageContainer className={ `change-profile-page ${ noBackgroundClass }` } state={ state } controller={ controller }>
        <PageContent/>
    </PageContainer>
  );
}

/**
 * @name getDefaultPageData
 * @param {Void} 
 * @returns {OBJECT}
 * @description Hook to return the default data required by the page
 */
export const getDefaultPageData = (profileID, profileType, context) => {
  const profiles = Profile.initAll();
  const inDesktopView = Util.inDesktopView;
  const inMabi = Util.inMabi;
  const { state } = context ?? {};
  const { branding } = state ?? {};
  const selectedProfileID = profileID ?? Profile.getDefaultBrandedProfile(branding).id;

  return {
      // The zelle iframe is shown on this page in abi22 standalone only.
      showZLEIframe: !inMabi,
      profiles,
      profileID: selectedProfileID,
      profileType: profileType ?? "personal"
  }
};

/**
* @name getDefaultPageUI
* @param {Void} 
* @return {OBJECT}
* @description Hook to return the default ui required by the page.
*/
export const getDefaultPageUI = (currentProfileSelectIsActive, showHeader, hidePageBody) => ({
  // no ui changes for now
  currentProfileSelectIsActive,
  hidePageBody: hidePageBody ?? false,
  showHeader: showHeader ?? true,
});

function PageContent(props) {
  const { data, ui, onChange, onSelectClick } = useStateManager();
  const { profileID, profileType } = data;
  const options = useModel().branding.options;
  const zleBusinessCID = options.zlebusiness_cid_abi22;
  let disableBusiness = "true";
  const zleConsumerCID = options.zle_courseID_abi22;
  let disableConsumer = "true";
  const subheaderText = options.zelle_subheader_abi22;
  const hasSubheaderText = subheaderText ? true : false;
  let zleProfileType = profileType;
  let zleProfileID = profileID;
  const showConsumerAccounts = options.show_consumer_accts_abi22 === "true";
  const showBusinessAccounts = options.show_business_accounts_abi22 === "true";


  // Enable the "Business" profile option if option "ZLE Business Course ID" is set.
  if (!(zleBusinessCID === "" || zleBusinessCID === null)) {
    disableBusiness = "false";
  }
  if (!(zleConsumerCID === "" || zleConsumerCID === null)) {
    disableConsumer = "false";
  }

  // If only one profile is available, set the profile type and id to that profile.
  if (zleConsumerCID === null && zleBusinessCID !== null && zleBusinessCID !== "") {
    zleProfileType = "business";
    zleProfileID = showBusinessAccounts ? "1": "0";

  } else if (zleBusinessCID === null && zleConsumerCID !== null && zleConsumerCID !== "") {
    zleProfileType = "personal";
    zleProfileID = showConsumerAccounts ? "0" : "1";
  }

  const profiles = Profile.initAll();

  return (
    <PageContentView showHeader={ ui.showHeader } hidePageBody={ ui.hidePageBody }>
      <div className="change-profile-page-container flex-container flex-vertical flex-width">
       <ProfilePicker ui={ui} profiles={profiles} profileID ={zleProfileID} mabiZelle={Util.inMabi}  onChange = {onChange} onSelectClick={onSelectClick} disableBusiness={ disableBusiness } disableConsumer={ disableConsumer }/>
        {/* ZLE iframe is only shown in desktop view. */}
        { data.showZLEIframe &&
        <WidgetContainer className="zelle-widget-container">
          { hasSubheaderText && <ContentSubheaderView text={ subheaderText } /> }
          <div className="iframe-container flex-container flex-horizontal justify-center">
            {/* iFrame for Zelle goes here */}
            
            <ExternalIframe className="zelle-iframe" demo="ZLE" zleProfileType={ zleProfileType } key="ChangeProfilePage"/>      
          </div>
        </WidgetContainer> 
        }
      </div>
    </PageContentView>
  );
}
